import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, AdminProductDetail, AdminProductListing, RedemptionRequest, WalletBalance, WalletsApi } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import { toNumber } from 'lodash';
import { getNetworkType, getWalletAddressForNetworkAssertExistence } from 'src/ui/helper/networks';
import { handleError } from 'src/ui/helper/error-handling';
import useTranslate from 'ui/hooks/use-translate';

interface AddRedemptionModalProps {
  onAddRedemption: () => void;
  onClose: () => void;
  products: AdminProductListing[];
  onSearchProduct: (val: string) => void;
}

interface AddRedemptionFields {
  product: string;
  walletAddress: string;
  units: string;
}

export const Form = makeModalForm<AddRedemptionFields>();

const AddRedemptionModal: FunctionComponent<AddRedemptionModalProps> = ({
  onAddRedemption,
  onClose,
  products,
  onSearchProduct,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<AdminProductDetail | undefined>(undefined);
  const [issuerWalletAddress, setIssuerWalletAddress] = useState('');
  const [walletBalance, setWalletBalance] = useState<WalletBalance[]>();

  const { makeAuthenticatedApi, withApi, error } = useApiCall();
  const walletsApi: WalletsApi = useMemo(() => makeAuthenticatedApi(WalletsApi), [makeAuthenticatedApi]);
  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  useEffect(() => {
    if (!selectedProduct) return;

    withApi(async () => {
      try {
        setWalletBalance(
          await walletsApi.walletsBalancesList({ token: selectedProduct.id, wallet: selectedProduct.issuerWallet.id }),
        );
      } catch {
        setWalletBalance(undefined);
      }
    });
  }, [withApi, walletsApi, selectedProduct]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddRedemptionFields>) => {
      const params: RedemptionRequest = {
        productId: fieldsValues.product,
        units: toNumber(fieldsValues.units),
        walletAddress: issuerWalletAddress,
      };

      withApi(async () => {
        await api.adminRedemptionsCreate({ redemptionRequest: params });

        setIssuerWalletAddress('');
        setSelectedProduct(undefined);
        onAddRedemption();
      });
    },
    [issuerWalletAddress],
  );

  const getWalletAddress = useCallback((product: AdminProductDetail | undefined) => {
    if (!product || !product.network || !product.issuerWallet) {
      setIssuerWalletAddress('');
      return;
    }

    const walletAddress = getWalletAddressForNetworkAssertExistence(
      getNetworkType(product.network),
      product.issuerWallet,
    );
    setIssuerWalletAddress(walletAddress);
  }, []);

  const getSelectedProductDetails = async (productId: string) => {
    const product = await api.adminProductsRetrieve({
      id: productId,
    });
    setSelectedProduct(product);
    getWalletAddress(product);
  };

  const { getErrorForField: getWalletsError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      blank: {
        field: 'issuer_wallet_address',
        translateKey: 'studioRedemptionsTable.addRedemptionModal.fields.walletAddress.noSourceWalletError',
      },
      issuer_has_no_wallet: {
        field: 'issuer_wallet_address',
        translateKey: 'studioRedemptionsTable.addRedemptionModal.fields.walletAddress.noSourceWalletError',
      },
    },
  });

  const { getErrorForField: getUnitsError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      not_enough_units: {
        field: 'units',
        translateKey: 'studioRedemptionsTable.addRedemptionModal.fields.units.requirements.max.error',
      },
    },
  });

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="studioRedemptionsTable.addRedemptionModal"
        onSubmit={(values: StateValues<AddRedemptionFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name="studioRedemptionsTable.addRedemption" />
        </ModalHeader>
        <ModalContent>
          <Form.ValueProvider>
            {({ units }) => (
              <>
                <Form.Group name="product" required={true}>
                  <Form.Select
                    options={products.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    }))}
                    onInputChange={onSearchProduct}
                    onChange={(productId: string) => {
                      getSelectedProductDetails(productId);
                    }}
                  />
                </Form.Group>
                {selectedProduct && (
                  <Form.Group name="walletAddress" fieldError={getWalletsError('issuer_wallet_address')}>
                    <Form.Input disabled={true} value={issuerWalletAddress} />
                  </Form.Group>
                )}
                <Form.Group name="units" required={true} fieldError={getUnitsError('units')}>
                  <Form.Input type="number" />
                  {walletBalance &&
                    Form.Validators.Range(
                      1,
                      walletBalance[0].balance,
                      toNumber(units) < 1 ? (
                        <Translate name="studioRedemptionsTable.addRedemptionModal.fields.units.requirements.min.error" />
                      ) : (
                        <Translate name="studioRedemptionsTable.addRedemptionModal.fields.units.requirements.max.error" />
                      ),
                      <Translate
                        name="studioRedemptionsTable.addRedemptionModal.fields.units.requirements.max.helper"
                        args={[walletBalance[0].balance]}
                      />,
                    )}
                  {Form.Validators.Integer()}
                </Form.Group>
              </>
            )}
          </Form.ValueProvider>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.close" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'add',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddRedemptionModal;
